import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { finalize } from 'rxjs';

import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { ConfirmationModalComponent } from 'app/shared/form-elements/components/confirmation-modal/confirmation-modal.component';
import { tableDts, hideCaptionBgBorder } from 'app/shared/design-tokens/table-dts';
import { BA_SharedInboxVm } from 'app/shared/generated/BA/Models/Admin/BA_SharedInboxVm';

/**
 * Shared inboxes on the attested invoice list: addresses that are not HOMER users, such as the business
 * office, that are emailed the PDF of every attested invoice.
 */
@Component({
    selector: 'pcg-ba-shared-inboxes'
    , templateUrl: './shared-inboxes.component.html'
    , styleUrls: ['./shared-inboxes.component.scss']
    , changeDetection: ChangeDetectionStrategy.OnPush
    , imports: [
        ReactiveFormsModule
        , ButtonModule
        , InputTextModule
        , SkeletonModule
        , TableModule
    ]
})
export class BaSharedInboxesComponent implements OnInit {

    http = inject(HttpClient);
    modal = inject(NgbModal);
    ns = inject(NotificationService);

    /** Null until the first load. */
    inboxes = signal<BA_SharedInboxVm[] | null>(null);
    isSaving = signal(false);
    isSubmitted = signal(false);

    form: UntypedFormGroup = BA_SharedInboxVm.Form;

    tableDts = { ...tableDts, ...hideCaptionBgBorder };

    ngOnInit() { this.getInboxes(); }

    get emailError(): string | null {
        const email = this.form.controls['email'];
        if (email.valid || !(email.touched || this.isSubmitted())) { return null; }
        return email.hasError('required') ? 'Enter an email address.' : 'Enter a valid email address.';
    }

    get nameError(): string | null {
        return this.form.controls['name'].hasError('maxlength') ? 'The name can be at most 100 characters.' : null;
    }

    getInboxes() {
        this.http.get<BA_SharedInboxVm[]>('api/BA/Contacts/GetSharedInboxes')
            .subscribe({
                next: (inboxes) => { this.inboxes.set(inboxes); }
                , error: () => {
                    this.inboxes.set([]);
                    this.ns.showError('The shared inboxes could not be loaded.');
                }
            });
    }

    add() {
        this.isSubmitted.set(true);
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isSaving.set(true);
        this.http.post<SystemMessage>('api/BA/Contacts/AddSharedInbox', {
            email: this.form.value.email?.trim()
            , name: this.form.value.name?.trim() || null
        })
            .pipe(finalize(() => this.isSaving.set(false)))
            .subscribe({
                next: (resp) => {
                    this.ns.showMessage(resp.message, resp.messageClass);
                    if (resp.isSuccessful) {
                        this.form.reset({ subscriptionSharedInboxId: 0 });
                        this.isSubmitted.set(false);
                        this.getInboxes();
                    }
                }
                , error: () => { this.ns.showError('The shared inbox could not be added.'); }
            });
    }

    confirmRemove(inbox: BA_SharedInboxVm) {
        const modal = this.modal.open(ConfirmationModalComponent);
        modal.componentInstance.modalRef = modal;
        modal.componentInstance.title = 'Remove Shared Inbox';
        modal.componentInstance.confirmWord = 'Remove';
        // Rendered as HTML by the modal, so the address is escaped.
        modal.componentInstance.confirmationMessage =
            `Stop emailing attested invoices to <strong>${this.escape(inbox.email)}</strong>?`;
        modal.result
            .then((isConfirmed: boolean) => { if (isConfirmed) { this.remove(inbox); } })
            .catch(() => { /* Dismissed */ });
    }

    remove(inbox: BA_SharedInboxVm) {
        this.http.post<SystemMessage>('api/BA/Contacts/RemoveSharedInbox', inbox.subscriptionSharedInboxId)
            .subscribe({
                next: (resp) => {
                    this.ns.showMessage(resp.message, resp.messageClass);
                    this.getInboxes();
                }
                , error: () => { this.ns.showError('The shared inbox could not be removed.'); }
            });
    }

    private escape(value: string) {
        const entities: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return (value ?? '').replace(/[&<>"']/g, (c) => entities[c]);
    }
}
